/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useChat } from './ChatContext';
import { useAuth } from './AuthContext';
import { WebRTCService } from '../services/webrtcService';
import toast from 'react-hot-toast';

const CallContext = createContext(null);

export function CallProvider({ children }) {
  const { user } = useAuth();
  const {
    addListener, initiateCall, sendOffer, sendAnswer,
    sendIceCandidate, acceptCall, rejectCall, endCall
  } = useChat();

  const [callState, setCallState] = useState('idle'); // 'idle' | 'outgoing' | 'incoming' | 'connecting' | 'connected' | 'ended'
  const [callType, setCallType] = useState('Voice'); // 'Voice' | 'Video'
  const [callId, setCallId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [remoteUser, setRemoteUser] = useState(null);
  const [duration, setDuration] = useState(0);

  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [cameraFacing, setCameraFacing] = useState('user');
  const [networkQuality, setNetworkQuality] = useState('Excellent');

  const webrtcRef = useRef(null);
  const timerRef = useRef(null);

  // Audio synthesis for Ringtone and Ringback
  const ringtoneAudioRef = useRef(null);

  const playRingtone = useCallback((type) => {
    stopRingtone();
    if (window.ShopAxoBridge?.startRingtone && type === 'incoming') {
      window.ShopAxoBridge.startRingtone();
    }
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(type === 'ringback' ? 440 : 880, audioCtx.currentTime);

      gain.gain.setValueAtTime(0.1, audioCtx.currentTime);
      osc.connect(gain);
      gain.connect(audioCtx.destination);

      osc.start();
      ringtoneAudioRef.current = { audioCtx, osc };
    } catch (e) {
      console.warn('[CallAudio] Audio context init failed:', e);
    }
  }, []);

  const stopRingtone = useCallback(() => {
    if (window.ShopAxoBridge?.stopRingtone) {
      window.ShopAxoBridge.stopRingtone();
    }
    if (ringtoneAudioRef.current) {
      try {
        ringtoneAudioRef.current.osc.stop();
        ringtoneAudioRef.current.audioCtx.close();
      } catch (e) {
        // ignore
      }
      ringtoneAudioRef.current = null;
    }
  }, []);

  // Cleanup helper
  const cleanUpCall = useCallback(() => {
    stopRingtone();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (webrtcRef.current) {
      webrtcRef.current.destroy();
      webrtcRef.current = null;
    }
    setLocalStream(null);
    setRemoteStream(null);
    setDuration(0);
    setIsMuted(false);
    setIsVideoOff(false);
    setNetworkQuality('Excellent');
    setCallState('idle');
    setCallId(null);
    setConversationId(null);
    setRemoteUser(null);
  }, [stopRingtone]);

  // Handle SignalR call signaling events
  useEffect(() => {
    if (!user) return;

    const unsub = addListener('CallSignaling', async (event, data) => {
      console.log(`[CallContext] Event: ${event}`, data);

      if (event === 'IncomingCall') {
        const callsAllowed = localStorage.getItem('user_setting_allow_calls') !== 'false' &&
                             localStorage.getItem('shop_setting_disable_calls') !== 'true';

        if (!callsAllowed) {
          console.log('[CallContext] Incoming call blocked: Calling option disabled in settings.');
          rejectCall(data.callId, data.callerId, 'rejected');
          return;
        }

        if (callState !== 'idle') {
          // Send busy signal if already on a call
          rejectCall(data.callId, data.callerId, 'busy');
          return;
        }

        setCallId(data.callId);
        setConversationId(data.conversationId);
        setCallType(data.callType || 'Voice');
        setRemoteUser({
          userId: data.callerId,
          name: data.callerName || 'Unknown Caller',
          avatar: data.callerAvatar
        });
        setCallState('incoming');
        playRingtone('incoming');
      }

      else if (event === 'CallAccepted') {
        stopRingtone();
        setCallState('connecting');

        // ONLY the caller generates SDP Offer after receiver accepts call
        const isCaller = user?.userId !== data.receiverId;
        if (isCaller && webrtcRef.current && data.receiverId) {
          try {
            const offer = await webrtcRef.current.createOffer();
            await sendOffer(data.callId, data.receiverId, JSON.stringify(offer));
          } catch (err) {
            console.error('[Call] Failed creating offer:', err);
            toast.error('Failed establishing call connection');
            cleanUpCall();
          }
        }
      }

      else if (event === 'ReceiveOffer') {
        // Receiver receives offer, responds with SDP Answer
        if (webrtcRef.current && data.callerId) {
          try {
            const answer = await webrtcRef.current.createAnswer(data.sdp);
            await sendAnswer(data.callId, data.callerId, JSON.stringify(answer));
            setCallState('connected');
            startDurationTimer();
          } catch (err) {
            console.error('[Call] Failed creating answer:', err);
            cleanUpCall();
          }
        }
      }

      else if (event === 'ReceiveAnswer') {
        // Caller receives answer
        if (webrtcRef.current) {
          try {
            await webrtcRef.current.setRemoteAnswer(data.sdp);
            setCallState('connected');
            startDurationTimer();
          } catch (err) {
            console.error('[Call] Failed setting remote answer:', err);
            cleanUpCall();
          }
        }
      }

      else if (event === 'ReceiveIceCandidate') {
        if (webrtcRef.current && data.candidate) {
          await webrtcRef.current.addIceCandidate(data.candidate);
        }
      }

      else if (event === 'CallRejected') {
        toast.error(`Call ${data.reason === 'busy' ? 'User is busy' : 'declined'}`);
        setCallState('ended');
        setTimeout(() => cleanUpCall(), 1500);
      }

      else if (event === 'CallEnded') {
        toast('Call ended');
        setCallState('ended');
        setTimeout(() => cleanUpCall(), 1000);
      }

      else if (event === 'UserBusy') {
        toast.error(data.reason || 'User is currently busy on another call');
        cleanUpCall();
      }

      else if (event === 'CallTimedOut') {
        toast.error(data.reason || 'Call timed out');
        setCallState('ended');
        setTimeout(() => cleanUpCall(), 1500);
      }
    });

    return () => unsub();
  }, [user, addListener, callState, playRingtone, stopRingtone, sendOffer, sendAnswer, rejectCall, cleanUpCall]);

  // Stop ringtone immediately whenever state moves out of incoming
  useEffect(() => {
    if (callState !== 'incoming') {
      stopRingtone();
    }
  }, [callState, stopRingtone]);

  // Check and prompt Chrome / Web Browser media permissions
  const checkOrRequestBrowserPermissions = async (type = 'Voice') => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return true;
    try {
      const isVideo = type.toLowerCase() === 'video';
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: isVideo
      });
      // Immediately stop temporary test tracks
      stream.getTracks().forEach(t => t.stop());
      return true;
    } catch (err) {
      console.warn('[CallPermissions] Browser permission prompt result:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        toast.error('Camera/Microphone permission blocked in browser. Please click lock icon in address bar to allow permissions.', { duration: 5000 });
      }
      return false;
    }
  };

  // Start duration timer during active call
  const startDurationTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setDuration(0);
    timerRef.current = setInterval(() => {
      setDuration(prev => prev + 1);
    }, 1000);
  };

  const startCall = async (targetUser, convId, type = 'Voice') => {
    const callsAllowed = localStorage.getItem('user_setting_allow_calls') !== 'false' &&
                         localStorage.getItem('shop_setting_disable_calls') !== 'true';

    if (!callsAllowed) {
      toast.error('Calling option is currently disabled in your settings. Please enable calling first.');
      return;
    }

    if (callState !== 'idle') {
      toast.error('You are already on a call');
      return;
    }

    setRemoteUser(targetUser);
    setConversationId(convId);
    setCallType(type);
    setCallState('outgoing');
    playRingtone('ringback');

    if (window.ShopAxoBridge?.requestMandatoryPermissions) {
      window.ShopAxoBridge.requestMandatoryPermissions();
    } else {
      await checkOrRequestBrowserPermissions(type);
    }

    try {
      // Initialize WebRTC media capture
      const rtc = new WebRTCService({
        onLocalStream: (stream) => setLocalStream(stream ? new MediaStream(stream.getTracks()) : null),
        onRemoteStream: (stream) => setRemoteStream(stream ? new MediaStream(stream.getTracks()) : null),
        onIceCandidate: (candidate) => {
          if (callIdRef.current && targetUser.userId) {
            sendIceCandidate(callIdRef.current, targetUser.userId, JSON.stringify(candidate));
          }
        },
        onConnectionStateChange: (state) => {
          if (state === 'connected') setNetworkQuality('Excellent');
          else if (state === 'disconnected') setNetworkQuality('Poor');
        }
      });

      webrtcRef.current = rtc;
      try {
        await rtc.initLocalMedia(type, cameraFacing);
      } catch (mediaErr) {
        console.warn('[CallContext] Outgoing video capture failed, falling back to audio-only:', mediaErr);
        await rtc.initLocalMedia('Voice', cameraFacing);
      }

      // Invoke SignalR hub method to initiate call
      const res = await initiateCall(targetUser.userId, convId, type);
      if (res && res.success && res.callId) {
        setCallId(res.callId);
        callIdRef.current = res.callId;
      } else {
        toast.error(res?.reason === 'busy' ? 'User is busy' : 'Call initiation failed');
        cleanUpCall();
      }
    } catch (err) {
      console.error('[CallContext] Start call error:', err);
      toast.error('Microphone/Camera permission required for calls.');
      cleanUpCall();
    }
  };

  const callIdRef = useRef(callId);
  useEffect(() => { callIdRef.current = callId; }, [callId]);

  // Answer Incoming Call
  const answerCall = async () => {
    if (callState !== 'incoming' || !callId || !remoteUser) return;
    stopRingtone();
    setCallState('connecting');

    if (window.ShopAxoBridge?.requestMandatoryPermissions) {
      window.ShopAxoBridge.requestMandatoryPermissions();
    } else {
      await checkOrRequestBrowserPermissions(callType);
    }

    try {
      const rtc = new WebRTCService({
        onLocalStream: (stream) => setLocalStream(stream ? new MediaStream(stream.getTracks()) : null),
        onRemoteStream: (stream) => setRemoteStream(stream ? new MediaStream(stream.getTracks()) : null),
        onIceCandidate: (candidate) => {
          sendIceCandidate(callId, remoteUser.userId, JSON.stringify(candidate));
        },
        onConnectionStateChange: (state) => {
          if (state === 'connected') setNetworkQuality('Excellent');
          else if (state === 'disconnected') setNetworkQuality('Poor');
        }
      });

      webrtcRef.current = rtc;
      try {
        await rtc.initLocalMedia(callType, cameraFacing);
      } catch (mediaErr) {
        console.warn('[CallContext] Answer video capture failed, falling back to audio-only:', mediaErr);
        await rtc.initLocalMedia('Voice', cameraFacing);
        toast('Connected in Audio-only mode (Camera access restricted)');
      }

      // Signal caller via SignalR that call was accepted
      await acceptCall(callId, remoteUser.userId);
    } catch (err) {
      console.error('[CallContext] Answer call error:', err);
      toast.error('Failed to access microphone/camera. Please check permissions.');
      cleanUpCall();
    }
  };

  // Reject Incoming Call
  const declineCall = async () => {
    if (callId && remoteUser) {
      await rejectCall(callId, remoteUser.userId, 'declined');
    }
    cleanUpCall();
  };

  // End Active / Outgoing Call
  const hangupCall = async () => {
    if (callId && remoteUser) {
      await endCall(callId, remoteUser.userId, duration, 'user_hung_up');
    }
    cleanUpCall();
  };

  // Media Controls
  const toggleMute = () => {
    const next = !isMuted;
    setIsMuted(next);
    if (webrtcRef.current) {
      webrtcRef.current.setAudioEnabled(!next);
    }
  };

  const toggleVideo = () => {
    const next = !isVideoOff;
    setIsVideoOff(next);
    if (webrtcRef.current) {
      webrtcRef.current.setVideoEnabled(!next);
    }
  };

  const flipCamera = async () => {
    if (webrtcRef.current) {
      const newFacing = await webrtcRef.current.switchCamera(callType);
      setCameraFacing(newFacing);
      toast(`Switched to ${newFacing === 'environment' ? 'Rear (Back)' : 'Front'} Camera`);
    }
  };

  return (
    <CallContext.Provider value={{
      callState, callType, callId, conversationId, remoteUser, duration,
      localStream, remoteStream, isMuted, isVideoOff, cameraFacing, networkQuality,
      startCall, answerCall, declineCall, hangupCall, toggleMute, toggleVideo, flipCamera
    }}>
      {children}
    </CallContext.Provider>
  );
}

export const useCall = () => useContext(CallContext);
